import { Alert, Button, Result, Typography } from 'antd';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { AppError, isAppError } from '../../api/errors';
import { NotFound } from './not-found';

/**
 * Where every unhandled failure lands (docs/07-implementation-roadmap.md F0.6, F0.7).
 *
 * The part that matters operationally is the support code: a 5xx tells staff
 * nothing they can act on, but the correlation ID turns "it broke this morning"
 * into one Kibana search (docs/90 §2.5). It is rendered copyable because the
 * alternative is a staff member transcribing a UUID over the phone.
 *
 * F1.10 adds the dedicated out-of-region screen for 403 — the interesting case
 * that must never be a generic "something went wrong".
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  // A router-level 404 (no route matched a data request) should read as the
  // 404 page, not as a crash.
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />;
  }

  const appError = isAppError(error) ? error : undefined;
  const title = appError ? titleFor(appError) : 'Terjadi kesalahan';
  const detail = appError?.userMessage ?? 'Sistem tidak dapat menampilkan halaman ini.';

  return (
    <Result
      status={appError?.kind === 'forbidden' ? 403 : 'error'}
      title={title}
      subTitle={detail}
      extra={[
        <Button key="retry" type="primary" onClick={() => navigate(0)}>
          Muat ulang halaman
        </Button>,
        <Button key="home" onClick={() => navigate('/')}>
          Kembali ke beranda
        </Button>,
      ]}
    >
      {appError?.correlationId ? <SupportCode correlationId={appError.correlationId} /> : null}
    </Result>
  );
}

function titleFor(error: AppError): string {
  switch (error.kind) {
    case 'forbidden':
      return 'Anda tidak memiliki akses';
    case 'notFound':
      return 'Data tidak ditemukan';
    case 'network':
      return 'Tidak dapat terhubung';
    // Not a crash and not this app's fault: the office's shared budget is
    // spent (F7.5). `userMessage` carries the wait and who it belongs to, and
    // the "muat ulang" below is the right action here — unlike the in-screen
    // alerts, this boundary has no query to refetch.
    case 'rateLimited':
      return 'Sistem sedang sibuk';
    default:
      return 'Terjadi kesalahan';
  }
}

function SupportCode({ correlationId }: { correlationId: string }) {
  return (
    <Alert
      type="info"
      showIcon={false}
      style={{ textAlign: 'left' }}
      title="Kode dukungan"
      description={
        <>
          <div>
            <Typography.Text code copyable={{ text: correlationId }}>
              {correlationId}
            </Typography.Text>
          </div>
          <Typography.Text type="secondary">
            Sertakan kode ini saat melapor ke tim teknis — dengan kode ini kejadiannya bisa ditelusuri di catatan
            server.
          </Typography.Text>
        </>
      }
    />
  );
}
